import { CookieConsentConfig, RichText } from "@/lib/nexus-app";

export const COOKIE_CONSENT_KEY = "cookie-consent-preferences";
export const DEFAULT_COOKIE_CONSENT_VERSION = 1;
export const ALLOWED_HREF_PREFIXES = [
  "https://",
  "http://",
  "/",
  "#",
  "mailto:",
  "tel:",
] as const;

export const BANNER_LINK_CLASS =
  "font-medium text-blue underline underline-offset-2 hover:text-blue-dark";
export const FOOTER_LINK_CLASS =
  "font-medium text-dark underline underline-offset-2 hover:text-blue";

export type CookieConsentMethod = "all" | "necessary";

export type CookieConsentPreferences = {
  version: number;
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  method: CookieConsentMethod;
  updatedAt: string;
};

export function isSafeHref(rawHref: string): boolean {
  const href = rawHref.trim().toLowerCase();
  return ALLOWED_HREF_PREFIXES.some((prefix) => href.startsWith(prefix));
}

export function toVersion(value: CookieConsentConfig["version"]): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1)
    return DEFAULT_COOKIE_CONSENT_VERSION;
  return Math.trunc(parsed);
}

export function isValidPreferences(
  parsed: Partial<CookieConsentPreferences>,
  expectedVersion: number
): parsed is CookieConsentPreferences {
  return (
    parsed.version === expectedVersion &&
    parsed.necessary === true &&
    typeof parsed.functional === "boolean" &&
    typeof parsed.analytics === "boolean" &&
    typeof parsed.marketing === "boolean" &&
    (parsed.method === "all" || parsed.method === "necessary") &&
    typeof parsed.updatedAt === "string"
  );
}

export function parseConsent(
  rawValue: string | null,
  expectedVersion: number
): CookieConsentPreferences | null {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as Partial<CookieConsentPreferences>;
    return isValidPreferences(parsed, expectedVersion) ? parsed : null;
  } catch {
    return null;
  }
}

export function createPreferences(
  method: CookieConsentMethod,
  version: number
): CookieConsentPreferences {
  return {
    version,
    necessary: true,
    functional: method === "all",
    analytics: method === "all",
    marketing: method === "all",
    method,
    updatedAt: new Date().toISOString(),
  };
}

export function hasRichTextContent(
  value: RichText | null | undefined
): boolean {
  return (
    Array.isArray(value) &&
    value.some(
      (segment) =>
        typeof segment.value === "string" && segment.value.trim().length > 0
    )
  );
}
